import { AsyncSelect, Box, FormGroup, Spinner } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { PractitionerName } from '@common/interfaces/practitionerName';
import type {
    CareProviderOrganizationsPractitioners,
    InitialOrganizationPractitioner,
    OrganizationPractitionerOptions,
} from '@frontend/components/elements/CareProviderSelection';
import type { IOrganization } from '@frontend/components/interfaces';
import { getDisplayName } from '@common/utils/getDisplayName';
import { buildLoadOrganizationsPractitionersUrl } from '@frontend/components/elements/careProviderSelector/utils/buildLoadOrganizationsPractitionersUrl';
import { createOrganizationLabel } from '@frontend/components/elements/careProviderSelector/utils/createOrganizationLabel';
import { loadOrganization } from '@frontend/components/elements/careProviderSelector/utils/loadOrganization';
import { loadPractitioner } from '@frontend/components/elements/careProviderSelector/utils/loadPractitioner';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';

type Practitioner = PractitionerName & {
    id: string;
};

type CareProviderFormInputProps = {
    error?: string;
    formlabeltext: string;
    hasErrors: boolean;
    initialOrganizationId: string | null;
    initialPractitionerId: string | null;
    onChange: (organizationId: string | null, practitionerId: string | null) => void;
    onDoValidate?: () => void;
    required: boolean;
    size: 'medium' | 'large';
};

export function CareProviderFormInput(props: CareProviderFormInputProps) {
    const translate = getSelectedLanguage();

    const [initialOrganizationPractitioner, setInitialOrganizationPractitioner] = useState<
        InitialOrganizationPractitioner | undefined
    >(undefined);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const loadInitialData = async () => {
            setIsLoading(true);
            await loadInitialOrganizationPractitioner();
            await loadOrganizationsPractitioners();
            setIsLoading(false);
        };

        loadInitialData();
    }, []);

    if (isLoading) {
        return (
            <Box
                alignItems="center"
                display="flex"
                justifyContent="center"
                minHeight="50px"
            >
                <Spinner />
            </Box>
        );
    }

    return (
        <FormGroup
            hasErrors={props.hasErrors}
            isRequired={props.required}
            label={props.formlabeltext}
        >
            <AsyncSelect
                defaultOptions
                defaultValue={
                    initialOrganizationPractitioner
                        ? {
                              label: getDisplayName(initialOrganizationPractitioner.practitioner),
                              value: `${props.initialOrganizationId}_${props.initialPractitionerId}`,
                          }
                        : null
                }
                error={getValidationErrorTranslation(props.error)}
                isClearable={false}
                loadingMessage={() => translate.common.loading}
                loadOptions={getOptions}
                name="generalPractitioner"
                noOptionsMessage={() => translate.common.noOptionsAvailable}
                onChange={async (selectedOption) => {
                    if (selectedOption && !Array.isArray(selectedOption)) {
                        await handleChange(selectedOption.value);
                    }
                }}
                size={props.size}
            />
        </FormGroup>
    );

    async function loadOrganizationsPractitioners(searchInput?: string): Promise<OrganizationPractitionerOptions[]> {
        try {
            const url = buildLoadOrganizationsPractitionersUrl(searchInput);
            const result = await fetchJson(url, 'GET');
            const { items } = result.json;

            const organizationWithGeneralPractitioners: OrganizationPractitionerOptions[] = items.map(
                (organization: CareProviderOrganizationsPractitioners) => ({
                    disabled: true,
                    label: createOrganizationLabel(organization),
                    practitioners: organization.practitioners.map((practitioner) => ({
                        label: getDisplayName(practitioner),
                        value: `${organization.id}_${practitioner.id}`,
                    })),
                    value: organization.id,
                })
            );

            return organizationWithGeneralPractitioners;
        } catch (error) {
            console.error('Failed to load organizations and practitioners:', error);
            throw error;
        }
    }

    async function getOptions(input: string) {
        const fetchedOptions = await loadOrganizationsPractitioners(input);

        const options = fetchedOptions.flatMap((organization) => [
            {
                className: organization.disabled ? 'Select__group-header is-disabled' : 'Select__group-header',
                isDisabled: organization.disabled,
                label: organization.label,
                value: organization.value,
            },
            ...organization.practitioners.map((practitioner) => ({
                className: 'Select__group-option',
                isDisabled: false,
                label: practitioner.label,
                value: practitioner.value,
            })),
        ]);

        return options;
    }

    async function loadInitialOrganizationPractitioner() {
        if (props.initialPractitionerId) {
            const practitioner: Practitioner = await loadPractitioner(props.initialPractitionerId);

            if (props.initialOrganizationId) {
                const organization: IOrganization = await loadOrganization(props.initialOrganizationId);

                setInitialOrganizationPractitioner({ ...organization, practitioner });
            }
        }
    }

    async function handleChange(value?: string) {
        await onChange(value);
        if (props.onDoValidate) {
            await props.onDoValidate();
        }
    }

    async function onChange(organizationPractitionerIds?: string) {
        if (!organizationPractitionerIds || !organizationPractitionerIds.includes('_')) {
            return await props.onChange(null, null);
        }
        const [selectedOrganizationId, selectedPractitionerId] = organizationPractitionerIds.split('_');
        await props.onChange(selectedOrganizationId, selectedPractitionerId);
    }
}
